import { normalize, words, stem } from './search-text'
import { toGregorian, jalaaliMonthLength } from './jalaali'
import { toHijri, fromHijri, hijriMonthLength } from './hijri'
import { J_MONTHS_IRANIAN, G_MONTHS, G_MONTHS_EN, H_MONTHS } from '../configs/names'
import { CalendarSystem } from './settings'

// Reading a search query: which words describe the thing, which say what kind
// of thing it is, and which say when. Pure — the day it is read against is
// passed in, so it can be tested on a fixed date. The rows the words are run
// over are built in services/search.ts.

// A happening is an occasion or an event: what the app calls رویداد.
export type SearchKind = 'task' | 'note' | 'event' | 'occasion' | 'happening'

// The days asked about: local midnights, `to` exclusive.
export interface IScope {
    from: Date
    to: Date
}

export interface IQuery {
    words: string[] // what to match, stemmed
    terms: string[] // the same words as typed, for the word vectors
    kind?: SearchKind // named outright — کارها, یادداشت‌ها
    holidays: boolean // تعطیلات: days off and nothing else
    scope?: IScope
}

// Today in every calendar, the shape calendar.today() hands out. wd is the
// Persian weekday, 0 = Saturday.
export interface IToday {
    gy: number
    gm: number
    gd: number
    jy: number
    jm: number
    jd: number
    wd: number
}

const HOLIDAY = new Set(['تعطیل', 'تعطیلی', 'تعطیلات'])

const KINDS: Record<string, SearchKind> = {
    'کار': 'task', 'وظیفه': 'task',
    'یادداشت': 'note',
    // The app's one word for the calendar's days and the reader's own events.
    'رویداد': 'happening',
    'قرار': 'event', 'جلسه': 'event', 'جلسات': 'event',
    'مناسبت': 'occasion',
}

// Words that ask rather than describe. The rows always show the date, so a
// date question needs nothing more than the thing's own name.
const STOP = new Set([
    'کی', 'چه', 'چی', 'چیه', 'کیه', 'روزی', 'تاریخ', 'تاریخش', 'هست', 'هستش', 'است',
    'باشه', 'میشه', 'شود', 'میشود', 'را', 'رو', 'به', 'از', 'در', 'با', 'که', 'این',
    'آن', 'اون', 'برای', 'من', 'ما', 'تو', 'شما', 'یه', 'یک', 'چند', 'کدام', 'کدوم',
    'چطور', 'چطوری', 'هم', 'تا', 'بود', 'داره', 'دارم', 'دارد', 'کن', 'کنم', 'کنید',
    'لطفا', 'بده', 'بگو', 'پیدا', 'همه', 'تمام', 'کل', 'لیست', 'فهرست',
    // On their own, once no time phrase has claimed them.
    'ماه', 'هفته', 'سال',
])

// A plural, for the intent words only: کارها asks for the same thing as کار.
const singular = (t: string) => t.replace(/(هایی|های|ها)$/, '')

// --- months ---

type Cal = 'j' | 'g' | 'h'

interface IMonth {
    cal: Cal
    month: number
}

// Every month name the three calendars go by, keyed without spaces so a name
// the half-space splits in two — ربیع‌الاول — is looked up from its two words
// put back together.
const MONTHS = new Map<string, IMonth>()
const learn = (name: string, cal: Cal, month: number) =>
    MONTHS.set(normalize(name).replace(/\s+/g, ''), { cal, month })
J_MONTHS_IRANIAN.forEach((n, i) => learn(n, 'j', i + 1))
G_MONTHS.forEach((n, i) => learn(n, 'g', i + 1))
G_MONTHS_EN.forEach((n, i) => learn(n, 'g', i + 1))
H_MONTHS.forEach((n, i) => learn(n, 'h', i + 1))
learn('امرداد', 'j', 5)
learn('مارچ', 'g', 3)
learn('اپریل', 'g', 4)
learn('جولای', 'g', 7)
learn('آگوست', 'g', 8)
learn('ربیع الاخر', 'h', 4)
learn('جمادی الاولی', 'h', 5)
learn('جمادی الاخره', 'h', 6)
learn('جمادی الثانیه', 'h', 6)
learn('ذی القعده', 'h', 11)
learn('ذوالقعده', 'h', 11)
learn('ذی الحجه', 'h', 12)
learn('ذوالحجه', 'h', 12)

const monthAt = (raw: string[], i: number): (IMonth & { consumed: number }) | undefined => {
    const pair = i + 1 < raw.length ? MONTHS.get(raw[i] + raw[i + 1]) : undefined
    if (pair) return { ...pair, consumed: 2 }
    const single = MONTHS.get(raw[i])
    return single ? { ...single, consumed: 1 } : undefined
}

// A four-digit year, in the calendar its size says — or the one the month
// beside it is in, when there is one. A hijri year is only read next to a
// hijri month: on its own 1448 is a Jalaali year.
const yearAt = (raw: string[], i: number, cal?: Cal): { cal: Cal, year: number } | undefined => {
    if (!/^\d{4}$/.test(raw[i] || '')) return
    const n = Number(raw[i])
    if (cal === 'h') return n >= 1300 && n <= 1499 ? { cal: 'h', year: n } : undefined
    if (n >= 1200 && n <= 1499 && cal !== 'g') return { cal: 'j', year: n }
    if (n >= 1900 && n <= 2200 && cal !== 'j') return { cal: 'g', year: n }
    return
}

// --- windows ---

const local = (gy: number, gm: number, gd: number) => new Date(gy, gm - 1, gd)
const shift = (d: Date, days: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + days)

const gregorian = (cal: Cal, y: number, m: number, d: number): Date => {
    if (cal === 'j') {
        const g = toGregorian(y, m, d)
        return local(g.gy, g.gm, g.gd)
    }
    if (cal === 'h') {
        const g = fromHijri(y, m, d)
        return local(g.gy, g.gm, g.gd)
    }
    return local(y, m, d)
}

const monthLength = (cal: Cal, y: number, m: number) =>
    cal === 'j' ? jalaaliMonthLength(y, m)
        : cal === 'h' ? hijriMonthLength(y, m)
            : new Date(y, m, 0).getDate()

const yearWindow = (cal: Cal, y: number): IScope =>
    ({ from: gregorian(cal, y, 1, 1), to: gregorian(cal, y + 1, 1, 1) })

const monthWindow = (cal: Cal, y: number, m: number): IScope => ({
    from: gregorian(cal, y, m, 1),
    to: m === 12 ? gregorian(cal, y + 1, 1, 1) : gregorian(cal, y, m + 1, 1),
})

// A day past the month's length lands on its last day, the way an anniversary
// does — a 31st asked of a 30-day month is that month's end, not nothing.
const dayWindow = (cal: Cal, y: number, m: number, d: number): IScope => {
    const from = gregorian(cal, y, m, Math.min(d, monthLength(cal, y, m)))
    return { from, to: shift(from, 1) }
}

const yearOf = (cal: Cal, today: IToday) =>
    cal === 'j' ? today.jy : cal === 'g' ? today.gy : toHijri(today.gy, today.gm, today.gd).hy

interface IFound {
    scope: IScope
    consumed: number
}

// A date written out: a day before a month name, a month name, or a year,
// each with a year after it when one is given. A month or a day without a
// year is the coming one — this year's if it is still ahead, else next
// year's — because a question about a day that has gone by is rare, and
// دیروز is there for it.
function dateAt(raw: string[], i: number, today: IToday): IFound | undefined {
    const now = local(today.gy, today.gm, today.gd).getTime()
    const day = /^\d{1,2}$/.test(raw[i]) ? Number(raw[i]) : 0
    if (day >= 1 && day <= 31) {
        const m = monthAt(raw, i + 1)
        if (!m) return
        const y = yearAt(raw, i + 1 + m.consumed, m.cal)
        if (y) return { scope: dayWindow(m.cal, y.year, m.month, day), consumed: 2 + m.consumed }
        let w = dayWindow(m.cal, yearOf(m.cal, today), m.month, day)
        if (w.from.getTime() < now) w = dayWindow(m.cal, yearOf(m.cal, today) + 1, m.month, day)
        return { scope: w, consumed: 1 + m.consumed }
    }
    const m = monthAt(raw, i)
    if (m) {
        const y = yearAt(raw, i + m.consumed, m.cal)
        if (y) return { scope: monthWindow(m.cal, y.year, m.month), consumed: m.consumed + 1 }
        let w = monthWindow(m.cal, yearOf(m.cal, today), m.month)
        if (w.to.getTime() <= now) w = monthWindow(m.cal, yearOf(m.cal, today) + 1, m.month)
        return { scope: w, consumed: m.consumed }
    }
    const y = yearAt(raw, i)
    return y ? { scope: yearWindow(y.cal, y.year), consumed: 1 } : undefined
}

const NEXT = new Set(['آینده', 'بعد', 'بعدی', 'دیگر'])
const PREV = new Set(['پیش', 'گذشته', 'قبل', 'قبلی'])

// Today and the days around it, and this week, month and year and the ones
// either side. The week runs Saturday to Friday; the month and the year are
// the calendar being read.
function relativeAt(raw: string[], i: number, today: IToday, system: CalendarSystem): IFound | undefined {
    const now = local(today.gy, today.gm, today.gd)
    const cal: Cal = system === 'gregorian' ? 'g' : 'j'
    const day = (offset: number): IScope => ({ from: shift(now, offset), to: shift(now, offset + 1) })
    const week = (offset: number): IScope => {
        const start = shift(now, -today.wd + 7 * offset)
        return { from: start, to: shift(start, 7) }
    }
    const month = (offset: number): IScope => {
        let y = yearOf(cal, today)
        let m = (cal === 'j' ? today.jm : today.gm) + offset
        if (m > 12) { m -= 12; y++ }
        if (m < 1) { m += 12; y-- }
        return monthWindow(cal, y, m)
    }
    const year = (offset: number): IScope => yearWindow(cal, yearOf(cal, today) + offset)
    const one = (scope: IScope): IFound => ({ scope, consumed: 1 })
    const two = (scope: IScope): IFound => ({ scope, consumed: 2 })
    const next = raw[i + 1] || ''
    const step = NEXT.has(next) ? 1 : PREV.has(next) ? -1 : 0

    switch (raw[i]) {
        case 'امروز': return one(day(0))
        case 'فردا': return one(day(1))
        case 'پسفردا': return one(day(2))
        case 'دیروز': return one(day(-1))
        case 'پریروز': return one(day(-2))
        case 'امسال': return one(year(0))
        case 'پارسال': return one(year(-1))
        case 'پس': return next === 'فردا' ? two(day(2)) : undefined
        case 'این':
            if (next === 'هفته') return two(week(0))
            if (next === 'ماه') return two(month(0))
            if (next === 'سال') return two(year(0))
            return
        case 'هفته': return step ? two(week(step)) : undefined
        case 'ماه': return step ? two(month(step)) : undefined
        case 'سال': return step ? two(year(step)) : undefined
    }
    return
}

// The query, read. Intent words and time words are taken out as they are
// met; what is left, minus the question words, is what to match.
export function parseQuery(text: string, today: IToday, system: CalendarSystem): IQuery {
    const raw = words(text)
    const out: IQuery = { words: [], terms: [], holidays: false }
    for (let i = 0; i < raw.length; i++) {
        const t = raw[i]
        const s = singular(t)
        if (HOLIDAY.has(s)) {
            out.holidays = true
            continue
        }
        if (KINDS[s]) {
            out.kind = KINDS[s]
            continue
        }
        const found = dateAt(raw, i, today) || relativeAt(raw, i, today, system)
        if (found) {
            out.scope = found.scope
            i += found.consumed - 1
            continue
        }
        if (STOP.has(t)) continue
        out.words.push(stem(t))
        out.terms.push(t)
    }
    return out
}
